<template>
  <v-container>
    <v-toolbar color="transparent" flat class="mb-3">
      <v-toolbar-title class="text-xl-h4 text-lg-h5 font-weight-bold">
       {{ $t('Documents.List.header') }}
      </v-toolbar-title>

      <v-spacer></v-spacer>
      
      <v-dialog v-model="showEditor" max-width="700px" persistent>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" dark class="mb-2" v-on="on">{{$t('Documents.Button.add')}}</v-btn>
        </template>
        <DocumentEditor 
          ref="documentEditor"
          :defaultDocument="editedIndex >= 0 ? documentsList[editedIndex] : null" 
          :formTitle="editedIndex >= 0 ? 'Modification' : 'Création'"
          @save="save"
          @cancel="close"
          :loading="saving"
        />
      </v-dialog>
    </v-toolbar>
    
    <v-row justify="space-around">
      <v-col cols="6">
        <SearchField 
          @search="value => search = value" 
          :help="$t('Documents.List.searchHint')"
        />
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="filteredDocuments"
            :items-per-page="$vuetify.breakpoint.lgAndDown ? 8 : 12"
            :footer-props="{'items-per-page-options': [8, 12, 25, 50]}"
            sort-by="updatedAt"
            sort-desc
            class="pt-5"
          >
            <template v-slot:item.icon>
              <v-icon>description</v-icon>
            </template>
            <template v-slot:item.fileSize="{ item }">
              {{ item.fileSizeFormatted }}
            </template>
            <template v-slot:item.updatedAt="{ item }">
              {{ item.updatedAtFormatted }}
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ item.createdAtFormatted }}
            </template>
            <template v-slot:item.isPublic="{ item }">
              <v-icon v-if="item.isPublic">check</v-icon>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn fab small icon :href="item.filePath" color="secondary">
                <v-icon small>cloud_download</v-icon>
              </v-btn>
              <v-btn fab small icon @click="editItem(item)" color="secondary">
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn fab small icon @click="deleteItem(item)" color="secondary">
                <v-icon small>delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import DocumentEditor from '@/components/Document/DocumentEditor'
import SearchField from '@/components/Utils/SearchField'
import prettyBytes from 'pretty-bytes'
import { FileMixin, DateMixin } from '@/mixins/index'
import { matchTextOnObject } from '@/helpers/search'
import { DocumentRepository } from '@portal/api'
export default {
  components: { DocumentEditor, SearchField },
  mixins: [FileMixin, DateMixin],
  data: () => ({
    documentsList: [],
    showEditor: false,
    search: '',
    editedIndex: -1,
    saving: false
  }),

  computed: {
    formattedDocuments () {
      return this.documentsList.map(document => ({
        ...document,
        updatedAtFormatted: this.$_date_format(document.updatedAt),
        createdAtFormatted: this.$_date_format(document.createdAt),
        fileSizeFormatted: prettyBytes(document.fileSize || 0),
        filePath: this.$_file_getDocumentPath(document.id)
      }))
    },
    filteredDocuments () {
      return this.formattedDocuments.filter(document => {
        return matchTextOnObject(this.search, document, ['title', 'description'])
      })
    },
    headers () {
      return [
        { sortable: false, value: 'icon', width: '50px' },
        {
          text: this.$t('Documents.Form.title'),
          align: 'left',
          value: 'title',
        },
        { text: this.$t('Documents.Form.size'), value: 'fileSize' },
        { text: this.$t('Global.Common.updatedAt'), value: 'updatedAt' },
        { text: this.$t('Global.Common.createdAt'), value: 'createdAt' },
        { text: this.$t('Documents.Form.public'), value: 'isPublic' },
        { sortable: false, value: 'actions', width: '200px' },
      ]
    }
  },

  watch: {
    showEditor (val) {
      val || this.close()
    }
  },

  methods: {
    async load () {
      this.documentsList = await DocumentRepository.fetchAll()
    },
    async save (document) {
      this.saving = true
      if (this.editedIndex > -1) {
        try {
          let updatedDocument = await DocumentRepository.update(document.id, document)
          if (document.file) {
            updatedDocument = await DocumentRepository.uploadFile(updatedDocument.id, document.file)
          }
          this.documentsList.splice(this.editedIndex, 1, updatedDocument)
          this.close()
        } catch (error) {
          this.$store.dispatch('notify/error', this.$t('Message.Error.update'))
        }
      } else {
        try {
          let newDocument = await DocumentRepository.create(document)
          if (document.file) {
            newDocument = await DocumentRepository.uploadFile(newDocument.id, document.file)
          }
          this.documentsList.push(newDocument)
          this.close()
        } catch (error) {
          this.$store.dispatch('notify/error', this.$t('Message.Error.create'))
        }
      }
      this.saving = false
    },
    async deleteItem (document) {
      const valid = confirm(this.$t('Documents.Message.delete'))
      if (valid) {
        await DocumentRepository.delete(document.id)
        const index = this.documentsList.findIndex(el => el.id === document.id)
        this.documentsList.splice(index, 1)
      }
    },
    editItem (item) {
      this.editedIndex = this.documentsList.findIndex(document => document.id === item.id)
      this.showEditor = true
    },
    close () {
      this.showEditor = false
      this.$refs.documentEditor.resetForm()
      setTimeout(() => {
        this.editedIndex = -1
      }, 300)
    },
  },

  created () {
    this.load()
  }
}
</script>